// SCR-STAFF-1006 (افزوده AP-INT-09؛ US-INT-104 تا 108): ماتریس اعتبارنامه بیمه‌گر × شعبه با ورود سه‌روشی (رمز و OTP،
// دولت من، QR)، تأیید شماره همراه نزد بیمه‌گر و حالت همکاری ساخد.
//
// هیچ توکن، گذرواژه، OTP یا نام رازی در این بخش نمایش یا ذخیره نمی‌شود؛ QR فقط در نشست خود پزشک دیده می‌شود
// (BR-INT-004، BR-INT-098).
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CredentialQrView,
  type InsurerLoginMethod,
  type ProviderCredentialRow,
  type ProviderCredentialStatus,
  type SakhadCooperationMode,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, LoadingState, SampleBadge, StatusChip, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { intKeys } from './common';

const STATUS_TONE: Record<ProviderCredentialStatus, StatusTone> = {
  NotConfigured: 'inactive',
  Active: 'active',
  ExpiringSoon: 'pending',
  Expired: 'error',
  Invalid: 'error',
  NeedsBranch: 'pending',
};

/** حالت‌های همکاری ساخد (AC-INT-105.1). */
const COOPERATION_MODES = ['Contracted', 'PrescribingOnly', 'NonContracted'] as const satisfies readonly NonNullable<SakhadCooperationMode>[];

/** کلید ردیف ماتریس: Adapter و شعبه (دامنه ارائه‌دهنده شعبه ندارد). */
const rowKey = (row: ProviderCredentialRow) => `${row.adapter}-${row.branchId ?? 'provider'}`;

/** ماتریس اعتبارنامه شخصی ارائه‌دهنده و جریان‌های ورود (AP-INT-09). */
export function ProviderCredentialMatrix() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('int.credential.manage_own');
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();

  const [openRow, setOpenRow] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [flowId, setFlowId] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [qr, setQr] = useState<CredentialQrView | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [mobileCode, setMobileCode] = useState('');

  const rows = useQuery({
    queryKey: intKeys.providerCredentials(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/provider-credentials')),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: intKeys.providerCredentials(session?.tenantId) });

  const reset = () => {
    setFlowId(null);
    setOtpSentTo(null);
    setOtp('');
    setQr(null);
    setRemaining(0);
    setPassword('');
  };

  // AC-INT-106.2: شمارش معکوس اعتبار QR؛ با رسیدن به صفر دکمه «QR جدید» جای آن را می‌گیرد.
  useEffect(() => {
    if (qr === null || remaining <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [qr, remaining]);

  const beginLogin = useMutation({
    mutationFn: (variables: { row: ProviderCredentialRow; method: InsurerLoginMethod }) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/logins', {
          body: {
            insurer: variables.row.insurer,
            method: variables.method,
            branchId: variables.row.branchId,
            username: username.trim() === '' ? null : username.trim(),
            password: password === '' ? null : password,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: (result) => {
      setPassword('');

      if (result.step === 'Redirect' && result.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }

      setFlowId(result.flowId);
      setOtpSentTo(result.otpSentTo ?? null);
      invalidate();
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
  });

  const submitOtp = useMutation({
    mutationFn: (variables: { flowId: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/logins/{flowId}:submit-otp', {
          params: { path: { flowId: variables.flowId } },
          body: { code: otp, password: null },
        }),
      ),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });

  const beginQr = useMutation({
    mutationFn: (row: ProviderCredentialRow) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/qr-logins', {
          body: { insurer: row.insurer, branchId: row.branchId },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: (result) => {
      setQr(result);
      setRemaining(Number(result.expiresInSeconds));
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
  });

  const pollQr = useMutation({
    mutationFn: (variables: { flowId: string }) =>
      unwrap(
        tenantApi.GET('/api/v1/integration/provider-credentials/qr-logins/{flowId}', {
          params: { path: { flowId: variables.flowId } },
        }),
      ),
    onSuccess: (result) => {
      if (result.state === 'Approved') {
        reset();
        invalidate();
      } else if (result.state === 'Cancelled') {
        reset();
      } else {
        setRemaining(Number(result.remainingSeconds ?? 0));
      }
    },
  });

  const startMobile = useMutation({
    mutationFn: (row: ProviderCredentialRow) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/mobile-verifications', {
          body: { insurer: row.insurer, branchId: row.branchId },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: (result) => {
      setOtpId(result.verified ? null : (result.otpId ?? null));
      invalidate();
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
  });

  const confirmMobile = useMutation({
    mutationFn: (variables: { row: ProviderCredentialRow; otpId: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/provider-credentials/mobile-verifications:confirm', {
          body: {
            insurer: variables.row.insurer,
            branchId: variables.row.branchId,
            otpId: variables.otpId,
            code: mobileCode,
          },
        }),
      ),
    onSuccess: () => {
      setOtpId(null);
      setMobileCode('');
      invalidate();
    },
  });

  const saveSakhad = useMutation({
    mutationFn: (variables: {
      row: ProviderCredentialRow;
      mode: SakhadCooperationMode;
      prescribingId: string;
      dispensingId: string;
    }) =>
      unwrap(
        tenantApi.PUT('/api/v1/integration/provider-credentials/sakhad', {
          body: {
            branchId: variables.row.branchId,
            mode: variables.mode,
            prescribingId: variables.prescribingId.trim() === '' ? null : variables.prescribingId.trim(),
            dispensingId: variables.dispensingId.trim() === '' ? null : variables.dispensingId.trim(),
          },
        }),
      ),
    onSuccess: invalidate,
  });

  if (rows.isPending) {
    return <LoadingState />;
  }

  if (rows.error) {
    return <ErrorState error={rows.error} onRetry={() => void rows.refetch()} />;
  }

  return (
    <Stack spacing={2} data-testid="provider-credential-matrix">
      <Typography variant="h6">{t('int:provider_credentials.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('int:provider_credentials.subtitle')}
      </Typography>

      {(rows.data?.items ?? []).map((row) => {
        const key = rowKey(row);
        const open = openRow === key;
        const methods = row.loginMethods ?? [];

        return (
          <Paper
            key={key}
            variant="outlined"
            sx={{ p: 2 }}
            data-testid={`provider-credential-row-${key}`}
            data-status={row.status}
            data-scope={row.scope}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle1">
                  {t(`int:insurer_credentials.insurer.${row.insurer}`, { defaultValue: row.insurer })}
                </Typography>
                <span data-testid={`provider-credential-status-${key}`}>
                  <StatusChip tone={STATUS_TONE[row.status]} labelKey={`int:provider_credentials.status.${row.status}`} />
                </span>
                {row.isSample ? <SampleBadge /> : null}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {t(`int:provider_credentials.scope.${row.scope}`)}
                {row.branchId ? (
                  <>
                    {t('common:list_separator')}
                    {t('int:provider_credentials.branch', { branch: row.branchId.slice(0, 8) })}
                  </>
                ) : null}
              </Typography>

              {row.expiresAt ? (
                <Typography variant="body2" color="text.secondary" data-testid={`provider-credential-expires-${key}`}>
                  {t('int:insurer_credentials.expires_at')} <JalaliDate value={row.expiresAt} withTime />
                </Typography>
              ) : null}

              {!row.mobileVerified ? (
                <Alert severity="warning" data-testid={`provider-credential-mobile-${key}`}>
                  {t('int:provider_credentials.mobile_required', { last4: row.mobileLast4 ?? '—' })}
                </Alert>
              ) : null}

              {row.cooperationMode === 'NonContracted' ? (
                <Alert severity="warning" data-testid={`provider-credential-non-contracted-${key}`}>
                  {t('int:provider_credentials.non_contracted')}
                </Alert>
              ) : null}

              {!row.canConnect ? (
                <Alert severity="info" data-testid={`provider-credential-not-configured-${key}`}>
                  {t('int:insurer_credentials.platform_not_configured')}
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SmartphoneOutlinedIcon />}
                  onClick={() => startMobile.mutate(row)}
                  disabled={!write.canWrite || startMobile.isPending}
                  data-testid={`provider-credential-verify-mobile-${key}`}
                >
                  {t('int:provider_credentials.verify_mobile')}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    reset();
                    setOpenRow(open ? null : key);
                  }}
                  disabled={!row.canConnect}
                  data-testid={`provider-credential-login-${key}`}
                >
                  {t('int:provider_credentials.login')}
                </Button>
              </Stack>

              {otpId !== null ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label={t('int:provider_credentials.mobile_code')}
                    value={mobileCode}
                    onChange={(event) => setMobileCode(event.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'provider-credential-mobile-code', inputMode: 'numeric' } }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => confirmMobile.mutate({ row, otpId })}
                    disabled={confirmMobile.isPending || mobileCode.trim() === ''}
                    data-testid="provider-credential-mobile-confirm"
                  >
                    {t('int:provider_credentials.confirm')}
                  </Button>
                </Stack>
              ) : null}

              {open ? (
                <>
                  <Divider />
                  <Stack spacing={1}>
                    {methods.includes('TaminOAuthPkce') ? (
                      <Alert severity="info">{t('int:provider_credentials.tamin_official_login')}</Alert>
                    ) : null}

                    {methods.includes('PasswordOtp') ? (
                      <Stack spacing={1}>
                        <TextField
                          size="small"
                          label={t('int:provider_credentials.username')}
                          value={username}
                          onChange={(event) => setUsername(event.target.value)}
                          slotProps={{ htmlInput: { 'data-testid': 'provider-credential-username' } }}
                        />
                        <TextField
                          size="small"
                          type="password"
                          label={t('int:provider_credentials.password')}
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          helperText={t('int:provider_credentials.password_not_stored')}
                          slotProps={{ htmlInput: { 'data-testid': 'provider-credential-password' } }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => beginLogin.mutate({ row, method: 'PasswordOtp' })}
                          disabled={!write.canWrite || beginLogin.isPending}
                          data-testid={`provider-credential-password-otp-${key}`}
                        >
                          {t('int:provider_credentials.send_otp')}
                        </Button>
                      </Stack>
                    ) : null}

                    {methods.includes('DolatMan') ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => beginLogin.mutate({ row, method: 'DolatMan' })}
                        disabled={!write.canWrite || beginLogin.isPending}
                        data-testid={`provider-credential-dolat-man-${key}`}
                      >
                        {t('int:provider_credentials.dolat_man')}
                      </Button>
                    ) : null}

                    {methods.includes('DolatManQr') ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCode2OutlinedIcon />}
                        onClick={() => beginQr.mutate(row)}
                        disabled={!write.canWrite || beginQr.isPending}
                        data-testid={`provider-credential-qr-${key}`}
                      >
                        {t('int:provider_credentials.qr_login')}
                      </Button>
                    ) : null}

                    {flowId !== null && qr === null ? (
                      <Stack spacing={1}>
                        {otpSentTo ? (
                          <Typography variant="body2" data-testid="provider-credential-otp-sent-to">
                            {t('int:provider_credentials.otp_sent_to', { mobile: otpSentTo })}
                          </Typography>
                        ) : null}
                        <TextField
                          size="small"
                          label={t('int:provider_credentials.otp')}
                          value={otp}
                          onChange={(event) => setOtp(event.target.value)}
                          slotProps={{ htmlInput: { 'data-testid': 'provider-credential-otp', inputMode: 'numeric' } }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => submitOtp.mutate({ flowId })}
                          disabled={submitOtp.isPending || otp.trim() === ''}
                          data-testid="provider-credential-otp-submit"
                        >
                          {t('int:provider_credentials.confirm')}
                        </Button>
                        {submitOtp.error ? <ErrorState error={submitOtp.error} compact /> : null}
                      </Stack>
                    ) : null}

                    {qr !== null ? (
                      <Stack spacing={1}>
                        <Typography variant="body2" data-testid="provider-credential-qr-payload" sx={{ wordBreak: 'break-all' }}>
                          {qr.qrPayload}
                        </Typography>
                        {remaining > 0 ? (
                          <Typography variant="caption" data-testid="provider-credential-qr-countdown">
                            {t('int:provider_credentials.qr_countdown', { seconds: faDigits(remaining) })}
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => beginQr.mutate(row)}
                            data-testid="provider-credential-qr-renew"
                          >
                            {t('int:provider_credentials.qr_renew')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => pollQr.mutate({ flowId: qr.flowId })}
                          disabled={pollQr.isPending}
                          data-testid="provider-credential-qr-poll"
                        >
                          {t('int:provider_credentials.qr_check')}
                        </Button>
                        {pollQr.error ? <ErrorState error={pollQr.error} compact /> : null}
                      </Stack>
                    ) : null}

                    {row.cooperationMode !== null && row.cooperationMode !== undefined ? (
                      <SakhadSection
                        row={row}
                        busy={saveSakhad.isPending}
                        canWrite={write.canWrite}
                        onSave={(mode, prescribingId, dispensingId) =>
                          saveSakhad.mutate({ row, mode, prescribingId, dispensingId })
                        }
                      />
                    ) : null}

                    {beginLogin.error ? <ErrorState error={beginLogin.error} compact /> : null}
                    {beginQr.error ? <ErrorState error={beginQr.error} compact /> : null}
                    {saveSakhad.error ? <ErrorState error={saveSakhad.error} compact /> : null}
                    {startMobile.error ? <ErrorState error={startMobile.error} compact /> : null}
                  </Stack>
                </>
              ) : null}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

/** حالت همکاری ساخد و شناسه‌های آن (AC-INT-105.1). */
function SakhadSection({
  row,
  busy,
  canWrite,
  onSave,
}: {
  row: ProviderCredentialRow;
  busy: boolean;
  canWrite: boolean;
  onSave: (mode: SakhadCooperationMode, prescribingId: string, dispensingId: string) => void;
}) {
  const { t } = useTranslation('int');
  const [mode, setMode] = useState<SakhadCooperationMode>(row.cooperationMode ?? 'NonContracted');
  const [prescribingId, setPrescribingId] = useState(row.prescribingId ?? '');
  const [dispensingId, setDispensingId] = useState(row.dispensingId ?? '');

  return (
    <Stack spacing={1} data-testid="provider-credential-sakhad">
      <TextField
        select
        size="small"
        label={t('int:provider_credentials.cooperation_mode')}
        value={mode}
        onChange={(event) => setMode(event.target.value as SakhadCooperationMode)}
        slotProps={{ htmlInput: { 'data-testid': 'provider-credential-sakhad-mode' } }}
      >
        {COOPERATION_MODES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`int:provider_credentials.cooperation.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label={t('int:provider_credentials.prescribing_id')}
        value={prescribingId}
        onChange={(event) => setPrescribingId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'provider-credential-prescribing-id' } }}
      />
      <TextField
        size="small"
        label={t('int:provider_credentials.dispensing_id')}
        value={dispensingId}
        onChange={(event) => setDispensingId(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'provider-credential-dispensing-id' } }}
      />
      <Button
        size="small"
        variant="contained"
        onClick={() => onSave(mode, prescribingId, dispensingId)}
        disabled={!canWrite || busy}
        data-testid="provider-credential-sakhad-save"
      >
        {t('common:actions.save')}
      </Button>
    </Stack>
  );
}
